import { useEffect, useRef, useCallback, useState } from 'react'

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'] as const

export interface UseActivityTrackerOptions {
  /** มิลลิวินาทีที่ idle แล้วจะ logout (default: 30 นาที) */
  inactivityTimeout?: number
  /** แสดงเตือนก่อนหมดเวลากี่มิลลิวินาที (default: 5 นาที) */
  warningTimeout?: number
  /** เปิด/ปิด activity tracking (เช่น sessionIdleMinutes === 0) */
  enabled?: boolean
  /** เรียกเมื่อหมดเวลา idle — ควร logout แล้ว redirect ไป /login */
  onLogout: () => void | Promise<void>
  /** เรียกเมื่อผู้ใช้กด "ขยายเวลา" — ถ้ามี refresh token ควรเรียกแล้ว reset timer */
  onExtendSession?: () => void | Promise<void>
}

/**
 * เหมือน user-classic: ติดตามการใช้งาน (mouse/keyboard/scroll/touch)
 * เมื่อ idle ครบ inactivityTimeout - warningTimeout จะแสดง modal เตือน
 * เมื่อ idle ครบ inactivityTimeout จะเรียก onLogout
 * รองรับ features.sessionIdleMinutes (0 = ปิด, 1–120 = นาที)
 */
export function useActivityTracker(options: UseActivityTrackerOptions) {
  const {
    inactivityTimeout = 30 * 60 * 1000,
    warningTimeout = 5 * 60 * 1000,
    enabled = true,
    onLogout,
    onExtendSession,
  } = options

  const lastActivityRef = useRef<number>(Date.now())
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [showSessionWarning, setShowSessionWarning] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(warningTimeout / 1000)
  const onLogoutRef = useRef(onLogout)
  const onExtendRef = useRef(onExtendSession)
  onLogoutRef.current = onLogout
  onExtendRef.current = onExtendSession

  const clearInactivityTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current)
      warningTimerRef.current = null
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current)
      logoutTimerRef.current = null
    }
  }, [])

  const resetActivityTimer = useCallback(() => {
    if (!enabled) return

    lastActivityRef.current = Date.now()
    clearInactivityTimers()

    warningTimerRef.current = setTimeout(() => {
      setShowSessionWarning(true)
      setTimeRemaining(warningTimeout / 1000)
    }, inactivityTimeout - warningTimeout)

    logoutTimerRef.current = setTimeout(async () => {
      await onLogoutRef.current()
    }, inactivityTimeout)
  }, [enabled, inactivityTimeout, warningTimeout, clearInactivityTimers])

  const handleActivity = useCallback(() => {
    if (enabled) resetActivityTimer()
  }, [enabled, resetActivityTimer])

  useEffect(() => {
    if (!enabled) {
      clearInactivityTimers()
      setShowSessionWarning(false)
      return
    }
    ACTIVITY_EVENTS.forEach((ev) => document.addEventListener(ev, handleActivity, true))
    resetActivityTimer()
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => document.removeEventListener(ev, handleActivity, true))
      clearInactivityTimers()
    }
  }, [enabled, handleActivity, resetActivityTimer, clearInactivityTimers])

  const extendSession = useCallback(async () => {
    setShowSessionWarning(false)
    clearInactivityTimers()
    try {
      if (onExtendRef.current) await onExtendRef.current()
      if (enabled) resetActivityTimer()
    } catch (error) {
      console.error('Failed to extend modern session:', error)
      await onLogoutRef.current()
    }
  }, [enabled, resetActivityTimer, clearInactivityTimers])

  const handleLogout = useCallback(async () => {
    setShowSessionWarning(false)
    clearInactivityTimers()
    await onLogoutRef.current()
  }, [clearInactivityTimers])

  return {
    showSessionWarning,
    timeRemaining,
    extendSession,
    handleLogout,
  }
}
